import { T, useTranslate } from '@tolgee/react';
import styled, { keyframes } from 'styled-components';

import { CrossSvg } from '../../../assets/icons';
import theme from '../../../assets/theme';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';

export type ExitToRegenerateDrawerProps = {
  reason: string;
  proposedLearningObjective?: string;
  onStartNewLesson: () => void;
  onDismiss: () => void;
};

/**
 * The refinement agent's way of saying "what you're asking for is a new lesson,
 * not an edit". Offers the fresh build, and carries the proposed objective over
 * so the teacher doesn't retype it. Designer (agent tab) only — the intent
 * refinement flow has no lesson to exit from.
 */
export function ExitToRegenerateDrawer({
  reason,
  proposedLearningObjective,
  onStartNewLesson,
  onDismiss,
}: ExitToRegenerateDrawerProps) {
  const { t } = useTranslate();

  return (
    <DrawerContainer>
      <DrawerHeader>
        <DrawerTitle>
          <T
            keyName="ExitToRegenerateDrawer.Title"
            defaultValue="This needs a new lesson"
          />
        </DrawerTitle>
        <DismissButton
          type="button"
          onClick={onDismiss}
          aria-label={t('Dismiss', 'Dismiss')}
        >
          <IconWrapper height="10px" width="10px" color={theme.colors.greyDark}>
            <CrossSvg />
          </IconWrapper>
        </DismissButton>
      </DrawerHeader>

      {reason && <ReasonText>{reason}</ReasonText>}

      {proposedLearningObjective && (
        <ProposedObjective>
          <ProposedObjectiveLabel>
            <T
              keyName="ExitToRegenerateDrawer.ProposedObjective"
              defaultValue="Suggested learning objective"
            />
          </ProposedObjectiveLabel>
          <span>{proposedLearningObjective}</span>
        </ProposedObjective>
      )}

      <DrawerActions>
        <Button type="ghost" onClick={onDismiss}>
          <T
            keyName="ExitToRegenerateDrawer.KeepEditing"
            defaultValue="Keep editing this lesson"
          />
        </Button>
        <Button type="orange" onClick={onStartNewLesson}>
          <T
            keyName="ExitToRegenerateDrawer.StartNewLesson"
            defaultValue="Start a new lesson"
          />
        </Button>
      </DrawerActions>
    </DrawerContainer>
  );
}

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const DrawerContainer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1002;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px 14px 14px;
  text-align: left;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.beigeDark};
  border-radius: 10px 10px 0 0;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  animation: ${slideUp} 0.25s ease-out;
  max-height: 70%;
  overflow-y: auto;
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const DrawerTitle = styled.span`
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${theme.colors.orangeDark};
`;

const DismissButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  padding: 0;
  background: none;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background-color: ${theme.colors.paleBeige};
  }
`;

const ReasonText = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  color: ${theme.colors.black};
`;

const ProposedObjective = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.black};
  background-color: ${theme.colors.paleBeige};
  border: 1px solid ${theme.colors.beigeDark};
  border-radius: 10px;
`;

const ProposedObjectiveLabel = styled.span`
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${theme.colors.greyDark};
`;

const DrawerActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
`;
